<script>
import { onMount, tick } from 'svelte';
import { getBounds } from '@typewriter/view';

export let items = [];
export let editor = null;
export let root = null;
export let paper = null;
export let range = null;
export let active = false;
export let inputMode = false;
export let pos = { left: 0, top: 0 };
export let href = '';

const SOURCE_USER = 'user';
const NEXT_HEADER = { undefined: 2, 2: 3, 3: undefined };
const NEXT_QUOTE = { undefined: 'alt', 'alt': true, true: undefined };
let input;
let menu

$: if (menu && root && range) recalculatePosition();
$: {
  items = [];

  if (paper) {
    const { blocks, marks } = paper;
    const { header, blockquote } = blocks.types;
    const { bold, italic, link } = marks.types;
    const format = editor.getFormat(range);

    if (bold) {
      items.push({
        name: 'bold',
        icon: 'format-bold',
        active: format.bold,
        action: onMarkClick
      });
    }

    if (italic) {
      items.push({
        name: 'italic',
        icon: 'format-italic',
        active: format.italic,
        action: onMarkClick
      });
    }

    if (link) {
      items.push({
        name: 'link',
        icon: 'link',
        active: format.link,
        action: onLinkClick
      });
    }

    if (items.length && (header || blockquote)) {
      items.push(null);
    }

    if (header) {
      items.push({
        name: 'header',
        icon: 'format-size',
        active: format.header === 2 || format.header === 3,
        activeFirst: format.header === 2,
        action: item => onVariableBlockClick('header', NEXT_HEADER, item)
      });
    }

    if (blockquote) {
      items.push({
        name: 'blockquote',
        icon: 'format-quote-open',
        active: format.blockquote,
        activeFirst: format.blockquote === 'alt',
        action: item => onVariableBlockClick('blockquote', NEXT_QUOTE, item)
      });
    }
  }
}

function recalculatePosition() {
  reposition();
  requestAnimationFrame(reposition);
}

function reposition() {
  if (!menu || !root || !range) {
    pos = { top: -100000, left: -100000 };
  } else {
    if (!menu.offsetParent) return; // Removed from DOM
    const container = menu.offsetParent.getBoundingClientRect();
    const targetRect = getBounds(root, paper, range);
    pos = {
      top: targetRect.top - container.top - menu.offsetHeight,
      left: targetRect.left - container.left + targetRect.width / 2 - menu.offsetWidth / 2,
    };
  }
}

function exitInput() {
  inputMode = false;
  href = '';
  editor.setSelection(range);
}

function createLink() {
  href = href.trim();
  if (href) {
    if (!href.startsWith('http')) {
      if (href.includes('.')) href = 'https://' + href;
    }
    editor.setSelection(range);
    editor.formatText(range, { link: href }, SOURCE_USER);
    if (editor.view) editor.view.render();
  }
  exitInput();
}

function onClick(item) {
  if (item.action) {
    item.action(item);
  }
}

function onKeyDown(event) {
  if (event.keyCode === 27) {
    event.preventDefault();
    event.stopPropagation();
    exitInput();
  } else if (event.keyCode === 13) {
    event.preventDefault();
    createLink();
  }
}

// Actions

async function inputLink() {
  inputMode = true;
  href = '';
  await tick();
  input.focus();
  await new Promise(resolve => setTimeout(resolve, 10));
  if (!input) return;
  // Ensure the window has focus, then the element
  input.ownerDocument.defaultView.focus();
  input.focus();
}

function onMarkClick(item) {
  editor.toggleTextFormat(range, { [item.name]: true }, SOURCE_USER);
  recalculatePosition();
}

function onBlockClick(item) {
  editor.toggleLineFormat(range, { [item.name]: item.value || true }, SOURCE_USER);
  recalculatePosition();
}

function onVariableBlockClick(name, NEXT, item) {
  const format = editor.getLineFormat(range);
  const nextFormat = { [name]: NEXT[format[name]] };
  editor.formatLine(range, nextFormat, SOURCE_USER);
  recalculatePosition();
}

function onLinkClick() {
  if (editor.getTextFormat(range).link) {
    editor.formatText(range, { link: null }, SOURCE_USER);
  } else {
    inputLink();
  }
}
</script>

<div
  bind:this={menu}
  style="top: {pos.top}px; left: {pos.left}px"
  class:active
  class:input-mode={inputMode}
  class="menu">
  <div class="items">
    {#each items as item}
      {#if item}
        <button class="editor-menu-{item.name}" class:active={item.active} class:active-first={item.activeFirst} on:click={() => onClick(item)} disabled={item.disabled}>
          <i class="icon icon-{item.icon}"></i>
          {#if item.activeFirst}
          <i class="icon icon-{item.icon}"></i>
          {/if}
        </button>
      {:else}
        <div class="separator"></div>
      {/if}
    {/each}
  </div>
  <div class="link-input">
    <input bind:this={input} bind:value={href} on:keydown={onKeyDown} on:blur={createLink} placeholder="https://example.com/">
    <i on:click={exitInput} class="close">×</i>
  </div>
</div>

<svelte:options accessors/>

<style>
  .menu {
    position: absolute;
    background-image: linear-gradient(to bottom, rgba(49, 49, 47, .99), #262625);
    border-radius: .25rem;
    opacity: 0;
    z-index: 10000;
    margin-top: -7px;
    white-space: nowrap;
  }
  .menu.active {
    opacity: 1;
    animation: pop-upwards 180ms forwards linear;
    transition: top 75ms ease-out, left 75ms ease-out;
  }
  .menu::after {
    content: '';
    position: absolute;
    bottom: -13px;
    left: 50%;
    margin-left: -7px;
    border: 7px solid transparent;
    border-top-color: #262625;
  }
  button {
    position: relative;
    height: 42px;
    width: 42px;
    padding: 9px;
    border: none;
    margin: 0;
    color: #fff;
    background: none;
    outline: none;
    cursor: pointer;
  }
  button:first-child {
    padding-left: 14px;
  }
  button:last-child {
    padding-right: 14px;
  }
  button.active .icon:first-child {
    color: #74b6ff;
  }
  button[disabled] {
    opacity: .5;
    cursor: default;
  }
  .icon {
    font-size: 24px;
  }

  button.active-first .icon:first-child {
    position: absolute;
    top: 9px;
    left: 9px;
  }
  button.active-first .icon-format-size:first-child {
    clip-path: polygon(0 0, 70% 0, 70% 30%, 42% 30%, 42% 100%, 0 100%);
  }
  button.active-first .icon-format-quote-open:first-child {
    clip-path: polygon(50% 0, 50% 100%, 100% 100%, 100% 0);
  }

  .separator {
    display: inline-block;
    vertical-align: middle;
    width: 1px;
    margin: 0 6px;
    height: 24px;
    background: rgba(0, 0, 0, .2);
    box-shadow: 1px 0 rgba(255, 255, 255, .2);
  }
  @keyframes pop-upwards {
    0% {
      transform: matrix(.97, 0, 0, 1, 0, 12);
      opacity: 0
    }
    20% {
      transform: matrix(.99, 0, 0, 1, 0, 2);
      opacity: .7
    }
    40% {
      transform: matrix(1, 0, 0, 1, 0, -1);
      opacity: 1
    }
    70% {
      transform: matrix(1, 0, 0, 1, 0, 0);
      opacity: 1
    }
    100% {
      transform: matrix(1, 0, 0, 1, 0, 0);
      opacity: 1
    }
  }

  .link-input {
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;
  }
  .link-input input {
    width: 100%;
    height: 100%;
    background: none;
    border: none;
    color: #f7f7f9;
    padding: 10px 24px 10px 12px;
    font-size: .875rem;
    outline: none;
    box-sizing: border-box;
  }
  .link-input .close {
    position: absolute;
    font-size: 16px;
    padding: 4px;
    right: 8px;
    top: 6px;
    color: #f7f7f9;
    opacity: .5;
    font-style: normal;
    cursor: pointer;
  }
  .link-input .close:hover {
    color: #f7f7f9;
    opacity: 1;
  }
  .menu:not(.input-mode) .link-input {
    display: none;
  }
  .menu.input-mode .items {
    opacity: 0;
  }
  .items {
    display: flex;
    align-items: center;
  }
</style>
